/**
 * Utility functions for geolocation capture
 */

/**
 * Check if location permissions are available - uses cached location for speed
 * @returns {Promise<{latitude: number, longitude: number}>} Cached coordinates
 * @throws {Error} If permission denied or location unavailable
 */
export const checkLocationPermission = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ latitude, longitude });
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location.";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "The request to get user location timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred while retrieving location.";
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: false,
        timeout: 3000,
        maximumAge: Infinity, // Use any cached location
      }
    );
  });
};

/**
 * Request user location permission and get coordinates with high accuracy
 * @returns {Promise<{latitude: number, longitude: number}>} User's coordinates
 * @throws {Error} If geolocation is not supported or permission is denied
 */
export const requestAndGetLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ latitude, longitude });
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location.";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "The request to get user location timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred while retrieving location.";
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
};

/**
 * Get location with acceptable cache age (for submission stamping)
 * @param {number} maxAgeSeconds - Maximum age of cached location in seconds (default 5 minutes)
 * @returns {Promise<{latitude: number, longitude: number}>} User's coordinates
 * @throws {Error} If geolocation is not supported or permission is denied
 */
export const getLocationForSubmission = (maxAgeSeconds = 300) => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        resolve({ latitude, longitude });
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location.";
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = "Location permission denied. Please enable location access in your browser settings.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = "Location information is unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage = "The request to get user location timed out.";
            break;
          default:
            errorMessage = "An unknown error occurred while retrieving location.";
        }
        
        reject(new Error(errorMessage));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: maxAgeSeconds * 1000, // Convert seconds to milliseconds
      }
    );
  });
};


/**
 * Format coordinates to a reasonable precision
 * @param {number} latitude
 * @param {number} longitude
 * @returns {object} Formatted coordinates
 */
export const formatCoordinates = (latitude, longitude) => {
  return {
    latitude: parseFloat(latitude.toFixed(6)),
    longitude: parseFloat(longitude.toFixed(6)),
  };
};

/**
 * Check if location has been captured
 * @param {object} location Object with latitude and longitude
 * @returns {boolean}
 */
export const hasValidLocation = (location) => {
  return (
    location &&
    typeof location.latitude === 'number' &&
    typeof location.longitude === 'number' &&
    !isNaN(location.latitude) &&
    !isNaN(location.longitude)
  );
};
